import { ref } from 'vue'
import type { UseCaseArticleImage, UseCaseArticleRecipe } from '@/data/useCases'

// One lightbox for the whole article: the pictures, recipe cards and tiles all call
// open() and the single dialog in ArticleLightbox.vue shows whatever is current.
// State is module-level so every caller shares it; nothing here touches the DOM
// until open() runs from a click, which keeps vite-ssg's prerender clear of it.
export type LightboxItem =
  | ({ kind: 'image' } & UseCaseArticleImage)
  | { kind: 'recipe'; recipe: UseCaseArticleRecipe }

const current = ref<LightboxItem | null>(null)
let invoker: HTMLElement | null = null

export function useArticleLightbox() {
  // `from` is the clicked element: focus goes back to it on close (Safari does not
  // focus a button on click, so the browser's own restore is not enough).
  function open(item: LightboxItem, from?: Event) {
    invoker = (from?.currentTarget as HTMLElement | null) ?? null
    current.value = item
  }
  function close() {
    current.value = null
  }
  // Called once the dialog has really closed: focus is blocked while a modal is open.
  // preventScroll: the page is already back where the reader left it.
  function returnFocus() {
    invoker?.focus({ preventScroll: true })
    invoker = null
  }
  // The element that opened the lightbox, so the page can be put back around it.
  const opener = () => invoker
  return { current, open, close, returnFocus, opener }
}
